import { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Star } from "lucide-react";
import { GOOGLE_REVIEWS, GOOGLE_RATING, GOOGLE_REVIEW_COUNT } from "../data/launch";
import SectionLabel from "./SectionLabel";
import Img from "./Img";
import PauseButton from "./PauseButton";

/** "From the field" — auto-scrolling slideshow of photos + attributed quotes.
 *  Hover reveals arrows and pauses the auto-scroll. */

type TickerItem =
  | { type: "photo"; src: string }
  /** google: a Google review (stars, links to Google). Otherwise an HXP Builder quote from the
   *  Youth Builders page: no stars, no Google link or "via Google Reviews" (marketing, Nov 2026). */
  | { type: "quote"; quote: string; name: string; role: string; google?: boolean };

/** Five Google reviews chosen by Steve (Website.docx, Nov 1), text verbatim, first names only. */
const tickerItems: TickerItem[] = [
  { type: "photo", src: "/images/wp/25years2-1-d0a93ebe.jpg" },
  {
    type: "quote",
    quote: "HXP has been a life changing experience for my family. I have now gone with two of my sons and they came back with increased love of other people/cultures, a love of service, and a deeper relationship with God. It is a challenging but uniquely rewarding experience. I highly recommend HXP trips for youth and parent builders!",
    name: "Cherie",
    role: "Parent builder",
    google: true,
  },
  { type: "photo", src: "/images/wp/dsc5192-e1709772731325-d0fed178.jpg" },
  {
    type: "quote",
    quote: "Hxp has given me life changing experiences that have made me gain so much confidence in all aspects of life. It's given me life long friends, and has strengthened my testimony so much. These trips show you who you really are and what you really need. I think every teenager should go on an hxp trip.",
    name: "Alayna",
    role: "Youth builder",
    google: true,
  },
  // Builder quotes moved from the Youth Builders page (marketing, Nov 2026)
  {
    type: "quote",
    quote: "Humanitarian Experience made me realize my self worth. It made me feel loved and a part of something bigger than myself. HXP changed my life in a way that I can't describe, I feel happier than I did before the trip, more confident and grateful.",
    name: "Laura (16) – Texas",
    role: "Dominican Republic Builder",
  },
  { type: "photo", src: "/images/wp/dsc3918-243f224f.jpg" },
  {
    type: "quote",
    quote: "This trip was absolutely amazing! I did not think I myself would change as much as I did! So great to reset and refocus on serving people! I recommend anyone able and willing go as a parent builder. If you can send your kid too! It has been such a great experience now for myself and two of our kids. We will be sending our other kids! Yes! Yes! Yes! Go! Miracles Happen!",
    name: "Jason",
    role: "Parent builder",
    google: true,
  },
  {
    type: "quote",
    quote: "HXP showed me how easy it can be to make meaningful friendships very quickly if you just jump in and be yourself. It was also an eye opening experience getting to see cultures and people from across the world. It helped give me some key experiences to build my testimony upon and helped provide an ideal environment to develop my relationship with Heavenly Father.",
    name: "Carson (16) – Texas",
    role: "Zimbabwe Builder",
  },
  { type: "photo", src: "/images/wp/dsc2535-e1710367239496-aed86b19.jpg" },
  {
    type: "quote",
    quote: "The HXP trip was a life changing experience for my son and me (I went as a \"Parent Builder\"/chaperone). Watching his testimony grow, seeing the youth bond over scripture discussions and service, and being influenced and encouraged by our one-in-a-million trip leaders have made this trip one I will never forget. Thank you!!",
    name: "Anne",
    role: "Parent builder",
    google: true,
  },
  {
    type: "quote",
    quote: "I made some of the best and strongest relationships that I have ever had. It helped me realize how good my life is and that I should be reaching out and trying to help others. It also showed me many changes that I need to make in my life if I want to keep feeling joy.",
    name: "Carter (17) – Utah",
    role: "Bolivia Builder",
  },
  { type: "photo", src: "/images/wp/dsc0176-e1739489653383-009a5da0.jpg" },
  {
    type: "quote",
    quote: "HXP was the best decision I've ever made! Everything about it was full of fun and overall an amazing experience. I met some of the best people ever both in my group and the locals. I fully believe this trip is a once in a lifetime opportunity and some of the things you learn you'll never be able to learn anywhere else. Also it's 100% true if you go on this trip THERE WILL BE MIRACLES!!! I strongly encourage anyone who is deciding to go to do it and you won't regret it I promise.",
    name: "Reese",
    role: "Youth builder",
    google: true,
  },
];

const Stars = ({ className, gap }: { className: string; gap: string }) => (
  <span className={`flex ${gap} text-hxp-red-dark`} aria-hidden="true">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className={className} fill="currentColor" strokeWidth={0} />
    ))}
  </span>
);

/** Google "G" mark, shown next to the review count as attribution. */
const GoogleG = ({ className }: { className: string }) => (
  <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

/** Quote text scales to fill the card: short quotes big, long quotes smaller.
 *  Thresholds sized so the attribution never clips off the card bottom. */
const quoteSize = (q: string) =>
  q.length <= 65 ? "text-2xl leading-snug" : q.length <= 115 ? "text-lg leading-snug" : "text-sm leading-relaxed";

const TestimonialsSection = () => {
  const trackRef = useRef<HTMLDivElement>(null);
  const paused = useRef(false);
  // the visible Pause button; kept apart from hover/focus so leaving the strip doesn't resume it
  const [userPaused, setUserPaused] = useState(false);
  const userPausedRef = useRef(false);
  userPausedRef.current = userPaused;

  // continuous auto-scroll with seamless loop (items are duplicated)
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    // Time-based so the speed is the same on 60Hz and 120Hz screens, and position is
    // tracked in JS and applied in whole pixels: iOS Safari drops sub-pixel scrollLeft
    // writes, which is why the strip used to sit still on phones.
    const PX_PER_SEC = 22;
    let raf = 0;
    let last = performance.now();
    let pos = trackRef.current?.scrollLeft ?? 0;
    const step = (now: number) => {
      const el = trackRef.current;
      const dt = Math.min(now - last, 100);
      last = now;
      if (el && !paused.current && !userPausedRef.current) {
        // follow the user if they scrolled the strip by hand
        if (Math.abs(el.scrollLeft - Math.floor(pos)) > 2) pos = el.scrollLeft;
        pos += (PX_PER_SEC * dt) / 1000;
        const half = el.scrollWidth / 2;
        if (pos >= half) pos -= half;
        el.scrollLeft = Math.floor(pos);
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  const nudge = (dir: number) =>
    trackRef.current?.scrollBy({ left: dir * 400, behavior: "smooth" });

  return (
    <section className="overflow-hidden border-t border-hxp-sand-dark bg-background py-16 md:py-20">
      {/* Rating leads, quietly: ink 5.0 about headline size, red stars, one calm line, headline beside it. */}
      <div className="mx-auto max-w-6xl px-6">
        <SectionLabel>From the field</SectionLabel>
        <div className="mt-8 grid grid-cols-1 gap-10 md:grid-cols-[auto_1fr] md:items-center md:gap-16">
          <a
            href={GOOGLE_REVIEWS}
            target="_self"
            className="group flex flex-col"
            aria-label={`Rated ${GOOGLE_RATING} out of 5 from ${GOOGLE_REVIEW_COUNT} Google reviews. Read them on Google`}
          >
            <span className="flex items-center gap-5">
              <span className="font-heading text-6xl leading-none text-foreground md:text-7xl">{GOOGLE_RATING}</span>
              <Stars className="h-7 w-7 md:h-8 md:w-8" gap="gap-1" />
            </span>
            <span className="mt-5 flex items-center gap-3">
              <GoogleG className="h-8 w-8 md:h-9 md:w-9" />
              <span className="font-heading text-4xl leading-none text-foreground md:text-5xl">{GOOGLE_REVIEW_COUNT}</span>
              <span className="btn-caps text-base leading-tight tracking-[0.12em] text-foreground/80 md:text-lg">
                Google
                <br />
                reviews
              </span>
            </span>
            <span className="mt-3 btn-caps text-sm tracking-[0.12em] text-primary underline-offset-4 group-hover:underline">
              Read them on Google →
            </span>
          </a>
          <h2 className="max-w-xl text-4xl md:border-l md:border-hxp-sand-dark md:pl-16 md:text-5xl">
            Hear it from <span className="text-primary">builders and parents</span>
          </h2>
        </div>
      </div>
      <div
        className="group relative mt-10"
        onMouseEnter={() => (paused.current = true)}
        onMouseLeave={() => (paused.current = false)}
        onFocus={() => (paused.current = true)}
        onBlur={() => (paused.current = false)}
      >
        <div className="pointer-events-none absolute bottom-0 left-0 top-0 z-10 w-12 bg-gradient-to-r from-background to-transparent" />
        <div className="pointer-events-none absolute bottom-0 right-0 top-0 z-10 w-12 bg-gradient-to-l from-background to-transparent" />

        {/* hover arrows */}
        <button
          onClick={() => nudge(-1)}
          aria-label="Previous"
          className="absolute left-4 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-hxp-sand-dark bg-background/95 text-foreground opacity-0 shadow-md transition-opacity hover:bg-hxp-cream group-hover:opacity-100 focus-visible:opacity-100"
        >
          <ArrowLeft size={18} />
        </button>
        <button
          onClick={() => nudge(1)}
          aria-label="Next"
          className="absolute right-4 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-hxp-sand-dark bg-background/95 text-foreground opacity-0 shadow-md transition-opacity hover:bg-hxp-cream group-hover:opacity-100 focus-visible:opacity-100"
        >
          <ArrowRight size={18} />
        </button>

        <div
          ref={trackRef}
          className="flex items-stretch gap-5 overflow-x-auto px-6"
          tabIndex={0}
          role="region"
          aria-label="Builder testimonials — scroll horizontally"
          style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
        >
          {[...tickerItems, ...tickerItems].map((item, i) =>
            item.type === "photo" ? (
              <Img
                sizes="320px"
                key={i}
                src={item.src}
                alt=""
                loading="lazy"
                className="h-80 w-auto shrink-0 self-center rounded-sm border border-hxp-sand-dark object-cover"
              />
            ) : (() => {
              const body = (
                <>
                  {item.google && (
                    <span className="flex gap-0.5 text-hxp-red-dark" aria-hidden="true">
                      {Array.from({ length: 5 }).map((_, k) => (
                        <Star key={k} className="h-4 w-4" fill="currentColor" strokeWidth={0} />
                      ))}
                    </span>
                  )}
                  <div className="flex flex-1 items-center">
                    <p className={`accent-hand text-foreground ${quoteSize(item.quote)}`}>
                      “{item.quote}”
                    </p>
                  </div>
                  <div className="pt-3">
                    <p className="btn-caps text-[11px] tracking-[0.14em] text-foreground">
                      {item.name}
                    </p>
                    <p className="btn-caps mt-0.5 text-[10px] tracking-[0.14em] text-primary">
                      {item.role}
                    </p>
                    {item.google && (
                      <p className="font-body mt-2 text-[11px] text-foreground/70">via Google Reviews</p>
                    )}
                  </div>
                </>
              );
              const cls =
                "flex min-h-80 w-[19rem] shrink-0 flex-col md:w-[26rem] rounded-sm border border-hxp-sand-dark bg-hxp-cream p-6";
              return item.google ? (
                <a key={i} href={GOOGLE_REVIEWS} target="_self" tabIndex={-1} className={`${cls} transition-colors hover:border-primary`}>
                  {body}
                </a>
              ) : (
                <div key={i} className={cls}>
                  {body}
                </div>
              );
            })()
          )}
        </div>
      </div>
      <div className="mx-auto mt-4 flex max-w-6xl justify-end px-6 motion-reduce:hidden">
        <PauseButton paused={userPaused} onToggle={() => setUserPaused((v) => !v)} what="testimonials" />
      </div>
    </section>
  );
};

export default TestimonialsSection;
